<template>
    <section class="modal-mask superadmin-modal modal-create-custom-survey">
        <div class="modal-mask-inner" @click="closeOpenModal"></div>
        <div class="modal-wrapper">
            <div class="modal-container">
                <div class="title">{{ `filters-from-q.${ isEdit ? 'edit' : 'convert' }-title` | lang }}</div>

                <div v-if="question" class="font-16">
                    <b>{{ question.text_no_number }}</b>
                </div>

                <div :class="['create-custom-survey-name-container', { 'has-error': filterNameAlreadyExists }]">
                    <label for="create-filter-input">{{ 'filters-from-q.input-label' | lang }}</label>
                    <input id="create-filter-input" type="text" v-model="filterName" class="create-filter-name"/>
                    <div class="hint">
                        <span v-if="!filterNameAlreadyExists">{{ 'filters-from-q.input-hint' | lang }}</span>
                        <span v-else class="text-error">{{ 'filters-from-q.input-error-exists' | lang }}</span>
                    </div>
                </div>

                <div class="create-filter-options">
                    <div class="font-16">
                        <b>{{ 'filters-from-q.options-title' | lang }}</b>
                    </div>

                    <template v-if="!showAnswerGrouping">
                        <div class="description-and-action">
                            <span class="font-14">{{ `filters-from-q.options-info-${isEdit ? 'edit' : 'convert'}` | lang }}</span>
                            <div class="new-btn" @click="switchToModify()">{{ 'filters-from-q.options-modify' | lang }}</div>
                        </div>

                        <div class="create-filter-answers">
                            <div v-for="answer in Object.values(question.option_objects)" class="create-filter-answer font-16" v-html="sanitize(answer.name)"></div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="description-and-action">
                            <span class="font-14">{{ 'filters-from-q.filter-grouping-info' | lang }}</span>
                            <div class="new-btn"
                                 @click="isEdit ? setupExistingFilterMapping() : setupNewFilterMapping()"
                            >{{ 'filters-from-q.filter-grouping-reset' | lang }}</div>
                        </div>

                        <div class="create-filter-groups">
                            <div class="create-filter-groups-row create-filter-groups-row-header">
                                <div class="cell">{{ 'filters-from-q.filter-grouping-answer-options' | lang }}</div>
                                <div class="cell-icon"><i class="material-icons">keyboard_arrow_right</i></div>
                                <div class="cell">{{ 'filters-from-q.filter-grouping-filter-options' | lang }}</div>
                            </div>

                            <div v-for="(option, index) of mapping" class="create-filter-groups-row">
                                <div class="cell" v-html="sanitize(option.mappedAgainst)"></div>
                                <div class="cell-icon"><i class="material-icons">keyboard_arrow_right</i></div>
                                <input type="text" class="cell cell-input" v-model="mapping[index].value"/>
                            </div>
                        </div>

                        <div class="font-14">{{ `filters-from-q.filter-grouping-tip-${isEdit ? 'edit' : 'convert'}` | lang }}</div>
                    </template>

                    <div class="font-14 text-deep-purple">{{ `filters-from-q.options-hint-${isSchool ? 'school' : 'network'}` | lang }}</div>
                </div>

                <div class="actions">
                    <div v-if="isEdit" @click="confirmDelete" class="new-btn delete" :class="{ 'is-creating': isDeleting }">
                        <span v-if="!isDeleting">{{ "general.delete" | lang }}</span>
                        <i v-else class="material-icons loading">autorenew</i>
                    </div>
                    <div @click="closeOpenModal"
                         :class="['new-btn', { 'is-disabled': isDeleting }]">{{ "general.cancel" | lang }}</div>
                    <div
                        @click="createOrUpdateFilter()"
                        :class="['new-btn', 'filled', { 'is-creating': isCreating, 'is-disabled': isMainActionDisabled }]"
                    >
                        <span v-if="!isCreating">{{ `${isEdit ? 'general.save' : 'filters-from-q.convert-button'}` | lang }}</span>
                        <i v-else class="material-icons loading">autorenew</i>
                    </div>
                </div>
            </div>
        </div>

        <Confirmation
            v-if="confirmation.show"
            :title="confirmation.title"
            :description="confirmation.description"
            :accept="confirmation.accept"
            :decline="confirmation.decline"
        />
    </section>
</template>

<script>
    const UI_DELAY = 3000;

    import sanitization from "@/mixins/sanitization"
    import filtersFromQuestions from "@/mixins/filters-from-questions"
    import Confirmation from "@/components/modals/results/Confirmation";

    export default {
        name: "ConvertToFilter",
        components: {Confirmation},
        props: ["questionId", "filterId", "existingFilterName"],
        mixins: [sanitization, filtersFromQuestions],
        computed: {
            isEdit() {
                return !!this.existingFilterName
            },

            isSchool() {
                return window.school_type === 'school'
            },

            isMainActionDisabled() {
                return this.filterNameAlreadyExists || !this.filterName || this.isDeleting
            },

            filterNameAlreadyExists() {
                return Object.keys(this.$store.state.filters.active)
                    .filter(k => this.isEdit ? k !== this.existingFilterName : true)
                    .includes(this.filterName.replace(/\s+/g,' ').trim())
            }
        },
        data() {
            return {
                confirmation: {
                    show: false,
                },
                isCreating: false,
                isDeleting: false,
                surveyPeriodId: this.$route.params.id,
                question: {
                    option_objects: {}
                },
                filter: {},
                filterName: this.existingFilterName ? this.existingFilterName : '',
                showAnswerGrouping: false,
                mapping: [],
            }
        },
        mounted() {
            this.loadFilterAndQuestion()
        },
        methods: {
            switchToModify() {
                this.showAnswerGrouping = true;
            },

            async loadFilterAndQuestion() {
                if (this.filterId) {
                    const { data } = await this.getFilter(this.$http, this.surveyPeriodId, this.filterId)
                    this.filter = data[0]
                }

                this.loadQuestion()
            },

            async loadQuestion() {
                const response = await this.$http.get(`${window.api_endpoint}/surveys/${this.surveyPeriodId}/questions/${this.questionId}`)
                this.question = response.body.data
                this.isEdit ? this.setupExistingFilterMapping() : this.setupNewFilterMapping()
            },

            setupNewFilterMapping() {
                this.mapping = []

                const options = JSON.parse(JSON.stringify(this.question.option_objects));
                Object.values(options).forEach(opt => {
                    this.mapping.push({
                        mappedAgainst: opt.name,
                        value: opt.name,
                        optionOrPropertyId: opt.id
                    })
                })
            },

            setupExistingFilterMapping() {
                this.mapping = []

                this.filter.mapping.forEach(filter => {
                    const optionIds = filter.question_options.map(f => f.question_option_id)
                    this.mapping.push({
                        mappedAgainst: this.getMappedAgainstFromIds(optionIds),
                        value: filter.question_options[0].value,
                        optionOrPropertyId: filter.property_value_id
                    })
                })
            },

            getMappedAgainstFromIds(optionIds) {
                let combinedOptionText = ''
                optionIds.map((id, index) => {
                    combinedOptionText += `${this.question.options_by_id[id]}, `
                    if (index === optionIds.length - 1) {
                        combinedOptionText = combinedOptionText.substring(0, combinedOptionText.length - 2);
                    }
                })
                return combinedOptionText;
            },

            createOrUpdateFilter() {
                if (this.isCreating) return

                this.isEdit ? this.updateFilter() : this.createFilter()
            },

            async createFilter() {
                this.isCreating = true;

                const body = {
                    name: this.filterName.replace(/\s+/g,' ').trim(),
                    question_id: this.questionId,
                    mapping: this.getMapping()
                }

                try {
                    await this.createFilterFromQuestion(this.surveyPeriodId, body)
                } catch(err) {
                    addEdNotification(err.body.message, "error")
                    this.isCreating = false;
                    return
                }

                await this.timeout(UI_DELAY);
                this.openInformativeModal()
                this.setupReloadFiltersWatcher(this.isEdit, this.$store.state.filters.count.legacy + 1)
            },

            async updateFilter() {
                this.isCreating = true;

                const body = {
                    name: this.filterName.replace(/\s+/g,' ').trim(),
                    question_id: this.questionId,
                    mapping: this.getMapping()
                }

                try {
                    await this.updateFilterFromQuestion(this.surveyPeriodId, this.filterId, body)
                } catch(err) {
                    addEdNotification(err.body.message, "error")
                    this.isCreating = false;
                    return
                }

                await this.timeout(UI_DELAY);
                this.openInformativeModal()
                this.setupReloadFiltersWatcher(this.isEdit, { filterId: this.filterId, expectedName: body.name })
            },

            getMapping() {
                const remapped = []
                const key = this.isEdit ? 'property_value_id' : 'question_option_id';

                this.mapping.forEach(opt => {
                    remapped.push({
                        value: opt.value,
                        [key]: opt.optionOrPropertyId
                    })
                })

                return remapped;
            },

            openInformativeModal() {
                this.openModal({
                    modal: 'Confirmation',
                    passedProps: {
                        title: this.$options.filters.lang(`filters-from-q.informative-${ this.isEdit ? 'edit' : 'convert' }-title`),
                        description: this.$options.filters.lang('filters-from-q.informative-description'),
                        decline: {
                            text: this.$options.filters.lang('cookies.ack'),
                            callback: () => this.closeOpenModal()
                        }
                    }
                })
            },

            confirmDelete() {
                this.confirmation = {
                    show: true,
                    title: this.$options.filters.lang(`filters-from-q.delete-filter-title`),
                    description: this.$options.filters.lang(`filters-from-q.delete-filter-description`),
                    accept: {
                        text: 'general.delete',
                        callback: this.doDeleteFilter
                    },
                    decline: {
                        text: 'general.cancel',
                        callback: this.resetConfirmation
                    }
                }
            },

            resetConfirmation() {
                this.confirmation = {
                    show: false,
                    title: '',
                    description: '',
                    accept: {},
                    decline: {}
                }
            },

            async doDeleteFilter() {
                this.confirmation.show = false;
                this.isDeleting = true;

                await this.deleteFilter(this.surveyPeriodId, this.filterId)
                delete this.$store.state.filters.active[this.existingFilterName]

                await this.reloadFilters()
                this.closeOpenModal()
            },
        }
    }
</script>

<style scoped lang="scss">
    @mixin font-16 {
        font-size: 16px;
        line-height: 24px;
    }

    .modal-create-custom-survey {
        .create-filter-name {
            margin-bottom: 4px;
            height: 56px;
        }

        .create-filter-options {
            margin: 0 -24px;
            padding: 24px;
            background: #F0F5EF;
        }

        .description-and-action {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 12px;
        }

        .create-filter-answer {
            margin-bottom: 12px;
            padding: 16px 24px;
            background: #F5FBF5;
        }

        .create-filter-groups {
            &-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding-bottom: 8px;

                .cell {
                    width: 100%;
                    padding: 16px;
                    @include font-16;
                    background: #F5FBF5;

                    &-input {
                        margin: 0;
                        height: auto;
                        background: transparent;
                        border-color: rgba($black-1, .1);
                    }

                    &-icon {
                        padding: 0 8px;
                        font-size: 24px;
                    }
                }

                &-header .cell {
                    background: $light-green;
                    font-weight: bold;
                }
            }
        }

        .font-14 {
            font-size: 14px;
            line-height: 24px;
        }

        .font-16 {
            @include font-16;
        }

        @include material-icon-loading;

        .modal-container {
            width: 560px;
            padding: 24px;
            background: #F5FBF5;
            color: #171D19;

            .title {
                padding-bottom: 24px;
                font-size: 24px;
                line-height: 32px;
                font-family: PlutoSansRegular;
                font-weight: 400;
            }

            $input-height: 56px;
            .create-custom-survey-name-container {
                position: relative;
                margin: 24px 0;

                &.has-error {
                    label {
                        color: $error;
                    }

                    input {
                        border-color: $error;
                    }
                }

                label {
                    position: absolute;
                    top: -5px;
                    left: 12px;
                    background: #F5FBF5;
                    padding: 0 4px;
                }

                .hint {
                    display: flex;
                    align-items: center;
                    font-size: .8rem;
                    line-height: 16px;
                    color: #9e9e9e;

                    span {
                        margin-left: 15px;
                    }
                }
            }

            .new-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 84px;
                margin-left: 8px;
                padding: 10px 12px;
                border-radius: 4px;
                font-size: 14px;
                line-height: 19px;
                font-weight: 500;
                text-transform: uppercase;
                font-family: PlutoSansMedium;
                cursor: pointer;
                color: #226A4C;

                &:hover {
                    background: #e9e9e9;
                }

                &.filled:not(.is-disabled) {
                    color: #fff;
                    background: #226A4C;
                }

                &.is-creating {
                    font-size: 18px;
                    text-align: center;
                }

                &.is-disabled {
                    pointer-events: none;
                    user-select: none;
                    color: rgba($black-1, .25);
                }

                &.delete {
                    margin-right: auto;

                    &:not(.is-creating) {
                        color: $error;
                    }
                }
            }

            .actions {
                display: flex;
                justify-content: flex-end;
                margin-top: 24px;
            }
        }
    }
</style>